/**
 * Shared Offline Types
 * Core types for offline functionality across desktop and mobile platforms
 */

// ============================================================================
// Sync Types
// ============================================================================

export type SyncStatus = 'idle' | 'syncing' | 'error' | 'offline';

export type OperationType = 'CREATE' | 'UPDATE' | 'DELETE';

export type ConflictStrategy = 'server-wins' | 'client-wins' | 'manual' | 'merge';

export interface QueuedOperation<T = unknown> {
  id: string;
  type: OperationType;
  entityType: string;
  entityId?: string;
  payload: T;
  timestamp: number;
  retryCount: number;
  maxRetries: number;
  status: 'pending' | 'syncing' | 'failed' | 'completed';
  errorMessage?: string;
  createdAt: number;
  lastAttemptAt?: number;
}

export interface SyncMetadata {
  entityType: string;
  lastSyncedAt: number;
  serverVersion?: string;
  localVersion?: string;
  conflictStrategy: ConflictStrategy;
}

export interface SyncResult {
  succeeded: number;
  failed: number;
  conflicts: SyncConflict[];
  timestamp: number;
}

export interface SyncConflict<T = unknown> {
  id: string;
  entityType: string;
  entityId: string;
  clientData: T;
  serverData: T;
  clientTimestamp: number;
  serverTimestamp: number;
  resolved: boolean;
  resolution?: 'client' | 'server' | 'merged';
  mergedData?: T;
}

// ============================================================================
// Cache Types
// ============================================================================

export interface CacheEntry<T = unknown> {
  data: T;
  entityType: string;
  entityId: string;
  cachedAt: number;
  expiresAt: number;
  version?: string;
  checksum?: string;
}

export interface CacheMetadata {
  key: string;
  entityType: string;
  entityId: string;
  cachedAt: number;
  expiresAt: number;
  size: number;
}

export interface CacheStats {
  totalEntries: number;
  totalSize: number;
  oldestEntry: number | null;
  newestEntry: number | null;
  expiredCount: number;
}

// ============================================================================
// Offline Entity Types
// ============================================================================

export type OfflineCapability = 'read' | 'write' | 'sync';

export interface OfflineEntityConfig {
  entityType: string;
  capabilities: OfflineCapability[];
  ttlSeconds: number;
  syncPriority: 'high' | 'medium' | 'low';
  conflictStrategy: ConflictStrategy;
  maxCacheSize?: number; // Max items to cache (LRU-evicted beyond this)
  /** Data minimization: if set, ONLY these fields are written to the local store. */
  cacheFields?: string[];
}

// Default configurations for offline-capable entities
export const OFFLINE_ENTITY_CONFIGS: Record<string, OfflineEntityConfig> = {
  'member-profile': {
    entityType: 'member-profile',
    capabilities: ['read', 'write', 'sync'],
    ttlSeconds: 86400, // 24 hours
    syncPriority: 'high',
    conflictStrategy: 'manual',
  },
  'enrollments': {
    entityType: 'enrollments',
    capabilities: ['read', 'sync'],
    ttlSeconds: 3600, // 1 hour
    syncPriority: 'high',
    conflictStrategy: 'server-wins',
  },
  'tasks': {
    entityType: 'tasks',
    capabilities: ['read', 'write', 'sync'],
    ttlSeconds: 1800, // 30 minutes
    syncPriority: 'high',
    conflictStrategy: 'server-wins',
  },
  'messages': {
    entityType: 'messages',
    capabilities: ['read', 'write', 'sync'],
    ttlSeconds: 3600,
    syncPriority: 'high',
    conflictStrategy: 'client-wins',
  },
  'documents': {
    entityType: 'documents',
    capabilities: ['read', 'write', 'sync'],
    ttlSeconds: 86400,
    syncPriority: 'medium',
    conflictStrategy: 'client-wins',
  },
  'referrals': {
    entityType: 'referrals',
    capabilities: ['read', 'write', 'sync'],
    ttlSeconds: 3600,
    syncPriority: 'medium',
    conflictStrategy: 'server-wins',
  },
  'calendar-events': {
    entityType: 'calendar-events',
    capabilities: ['read', 'write', 'sync'],
    ttlSeconds: 1800,
    syncPriority: 'medium',
    conflictStrategy: 'manual',
  },
  'case-notes': {
    entityType: 'case-notes',
    capabilities: ['read', 'write', 'sync'],
    ttlSeconds: 3600,
    syncPriority: 'high',
    conflictStrategy: 'client-wins',
  },
  'notifications': {
    entityType: 'notifications',
    capabilities: ['read'],
    ttlSeconds: 1800,
    syncPriority: 'low',
    conflictStrategy: 'server-wins',
  },
};

// ============================================================================
// Sync Protocol Types
// ============================================================================

export interface SyncPullRequest {
  entityTypes: string[];
  lastSyncTimestamps: Record<string, number>;
  limit?: number;
  includeDeleted?: boolean;
}

export interface SyncPullResponse {
  changes: SyncChange[];
  serverTimestamp: number;
  hasMore: boolean;
}

export interface SyncChange<T = unknown> {
  entityType: string;
  entityId: string;
  operation: OperationType;
  data: T | null; // null for DELETE
  timestamp: number;
  version: string;
}

export interface SyncPushRequest {
  operations: QueuedOperation[];
  clientTimestamp: number;
}

export interface SyncPushResponse {
  results: SyncOperationResult[];
  serverTimestamp: number;
}

export interface SyncOperationResult {
  operationId: string;
  status: 'success' | 'conflict' | 'error';
  data?: unknown;
  serverData?: unknown;
  errorMessage?: string;
}

// ============================================================================
// Offline State Types
// ============================================================================

export interface OfflineState {
  /** Effective online = navigator.onLine AND not forced offline by the user. */
  isOnline: boolean;
  /** User chose "Work Offline" manually (independent of network connectivity). */
  forcedOffline: boolean;
  syncStatus: SyncStatus;
  lastSyncAt: number | null;
  pendingOperations: number;
  conflicts: SyncConflict[];
  error: string | null;
}

export interface OfflineContextValue extends OfflineState {
  // Actions
  queueOperation: <T>(
    type: OperationType,
    entityType: string,
    entityId: string | undefined,
    payload: T
  ) => Promise<string>;
  syncNow: () => Promise<SyncResult>;
  resolveConflict: (
    conflictId: string,
    resolution: 'client' | 'server' | 'merged',
    mergedData?: unknown
  ) => Promise<void>;
  clearCache: () => Promise<void>;
  getCacheStats: () => Promise<CacheStats>;

  // Cache operations
  getCached: <T>(entityType: string, entityId: string) => Promise<T | null>;
  setCached: <T>(entityType: string, entityId: string, data: T) => Promise<void>;
  invalidateCache: (entityType: string, entityId?: string) => Promise<void>;
}

// ============================================================================
// Utility Types
// ============================================================================

export interface RetryConfig {
  maxRetries: number;
  baseDelayMs: number;
  maxDelayMs: number;
  backoffMultiplier: number;
}

export const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxRetries: 3,
  baseDelayMs: 1000,
  maxDelayMs: 30000,
  backoffMultiplier: 2,
};

// ============================================================================
// Helper Functions
// ============================================================================

export function generateOperationId(): string {
  return `op_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

export function calculateRetryDelay(
  retryCount: number,
  config: RetryConfig = DEFAULT_RETRY_CONFIG
): number {
  const delay = config.baseDelayMs * Math.pow(config.backoffMultiplier, retryCount);
  return Math.min(delay, config.maxDelayMs);
}

export function isExpired(expiresAt: number): boolean {
  return Date.now() > expiresAt;
}

export function calculateExpiresAt(ttlSeconds: number): number {
  return Date.now() + ttlSeconds * 1000;
}

export function getEntityConfig(entityType: string): OfflineEntityConfig | undefined {
  return OFFLINE_ENTITY_CONFIGS[entityType];
}

export function canWriteOffline(entityType: string): boolean {
  const config = getEntityConfig(entityType);
  return config?.capabilities.includes('write') ?? false;
}

export function canSyncEntity(entityType: string): boolean {
  const config = getEntityConfig(entityType);
  return config?.capabilities.includes('sync') ?? false;
}
